import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileCheck,
  FileText,
  Loader2,
  MapPin,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Upload,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  listJobDescriptions,
  parseJobDescriptionUpload,
  type ApiJobDescriptionStatus,
  type ApiJobDescriptionSummary,
} from '../../libs/api/jobDescriptionService';
import { getApiErrorMessage } from '../../libs/api/errorHandler';
import { JobApplicantsModal } from './JobApplicantsModal';
import { buildActiveCandidatesUrl } from '../candidates/candidateListUrl';

const PAGE_SIZE = 10;

const mapStatusFilterToApi = (value: string): ApiJobDescriptionStatus | undefined => {
  if (value === 'active') return 'ACTIVE';
  if (value === 'draft') return 'DRAFT';
  if (value === 'closed') return 'CLOSE';
  return undefined;
};

const STATUS_BADGES: Record<
  ApiJobDescriptionStatus,
  { label: string; className: string; dot: string }
> = {
  ACTIVE: {
    label: 'Active',
    className: 'border-success/30 bg-success-muted text-success-muted-foreground',
    dot: 'bg-success',
  },
  DRAFT: {
    label: 'Draft',
    className: 'border-border-subtle bg-muted text-text-subtle',
    dot: 'bg-text-subtle',
  },
  CLOSE: {
    label: 'Closed',
    className: 'border-error/30 bg-error-muted text-error-muted-foreground',
    dot: 'bg-error',
  },
};

const formatCreatedDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const JDManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [departmentFilter, setDepartmentFilter] = React.useState('all');
  const [isUploadingJd, setIsUploadingJd] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [applicantsJd, setApplicantsJd] = React.useState<ApiJobDescriptionSummary | null>(null);
  const jdUploadInputRef = React.useRef<HTMLInputElement>(null);

  const handleUploadJdClick = () => {
    if (isUploadingJd) return;
    jdUploadInputRef.current?.click();
  };

  const handleUploadJdChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsUploadingJd(true);
    try {
      const result = await parseJobDescriptionUpload(file);
      navigate('/jobs/new', { state: { prefill: result.jd } });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to read JD file'));
    } finally {
      setIsUploadingJd(false);
    }
  };

  const {
    data: jdResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['job-descriptions', { page: 1, page_size: 100 }],
    queryFn: () => listJobDescriptions({ page: 1, page_size: 100 }),
  });

  React.useEffect(() => {
    if (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load job descriptions'));
    }
  }, [error]);

  const jds = React.useMemo<ApiJobDescriptionSummary[]>(
    () => jdResponse?.items ?? [],
    [jdResponse],
  );

  const activeCount = React.useMemo(() => jds.filter((jd) => jd.status === 'ACTIVE').length, [jds]);
  const draftCount = React.useMemo(() => jds.filter((jd) => jd.status === 'DRAFT').length, [jds]);
  const totalApplicants = React.useMemo(
    () => jds.reduce((sum, jd) => sum + (jd.candidate_count ?? 0), 0),
    [jds],
  );
  const departmentOptions = React.useMemo(
    () =>
      Array.from(new Set(jds.map((jd) => jd.department).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [jds],
  );
  const filteredJds = React.useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    return jds.filter((jd) => {
      const matchesSearch =
        keyword.length === 0 ||
        jd.title.toLowerCase().includes(keyword) ||
        jd.department.toLowerCase().includes(keyword) ||
        jd.location.toLowerCase().includes(keyword);
      const matchesStatus =
        statusFilter === 'all' || jd.status === mapStatusFilterToApi(statusFilter);
      const matchesDepartment = departmentFilter === 'all' || jd.department === departmentFilter;

      return matchesSearch && matchesStatus && matchesDepartment;
    });
  }, [departmentFilter, jds, searchTerm, statusFilter]);

  const isFilterActive =
    searchTerm.trim().length > 0 || statusFilter !== 'all' || departmentFilter !== 'all';

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleDepartmentFilterChange = (value: string) => {
    setDepartmentFilter(value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filteredJds.length / PAGE_SIZE));
  // Clamp instead of syncing state: the list can shrink (e.g. after a refetch) while on a later page.
  const page = Math.min(currentPage, totalPages);
  const pageStart = (page - 1) * PAGE_SIZE;
  const paginatedJds = filteredJds.slice(pageStart, pageStart + PAGE_SIZE);

  const summaryItems = [
    {
      key: 'total',
      label: 'Total JDs',
      value: jds.length,
      icon: FileText,
      iconClassName: 'bg-primary-light text-primary',
      valueClassName: 'text-text-main',
      isSelected: statusFilter === 'all',
      onClick: () => handleStatusFilterChange('all'),
    },
    {
      key: 'applicants',
      label: 'Total Applicants',
      value: totalApplicants,
      icon: Users,
      iconClassName: 'bg-primary-light text-primary',
      valueClassName: 'text-text-main',
      isSelected: false,
      onClick: undefined,
    },
    {
      key: 'active',
      label: 'Active JDs',
      value: activeCount,
      icon: Briefcase,
      iconClassName: 'bg-success-muted text-success',
      valueClassName: 'text-success',
      isSelected: statusFilter === 'active',
      onClick: () => handleStatusFilterChange(statusFilter === 'active' ? 'all' : 'active'),
    },
    {
      key: 'draft',
      label: 'Draft JDs',
      value: draftCount,
      icon: FileCheck,
      iconClassName: 'bg-muted text-text-subtle',
      valueClassName: 'text-text-subtle',
      isSelected: statusFilter === 'draft',
      onClick: () => handleStatusFilterChange(statusFilter === 'draft' ? 'all' : 'draft'),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-main">Job Descriptions</h1>
          <p className="text-text-subtle">
            Manage and create job requirements for your recruitment pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleUploadJdClick} disabled={isUploadingJd}>
            {isUploadingJd ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {isUploadingJd ? 'Reading JD file...' : 'Upload JD'}
          </Button>
          <input
            ref={jdUploadInputRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            disabled={isUploadingJd}
            onChange={(event) => {
              void handleUploadJdChange(event);
            }}
          />
          <Button onClick={() => navigate('/jobs/new')}>
            <Plus size={16} /> Create New JD
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-1 rounded-xl border border-border-subtle bg-card p-1.5 shadow-sm md:grid-cols-4">
        {summaryItems.map((item) => {
          const content = (
            <>
              <div className={`rounded-lg p-2 ${item.iconClassName}`}>
                <item.icon size={18} />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-text-subtle">
                  {item.label}
                </div>
                <div className={`text-xl font-bold ${item.valueClassName}`}>{item.value}</div>
              </div>
            </>
          );
          const baseClassName = 'flex items-center gap-3 rounded-lg px-3 py-2 text-left';

          return item.onClick ? (
            <button
              key={item.key}
              type="button"
              aria-pressed={item.isSelected}
              onClick={item.onClick}
              className={`${baseClassName} cursor-pointer transition-colors ${
                item.isSelected ? 'bg-primary-light/60' : 'hover:bg-bg-main'
              }`}
            >
              {content}
            </button>
          ) : (
            <div key={item.key} className={baseClassName}>
              {content}
            </div>
          );
        })}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border-subtle bg-card p-3 shadow-sm">
        <div className="relative min-w-55 max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" size={16} />
          <Input
            type="text"
            placeholder="Search job descriptions..."
            className="bg-bg-main pl-9"
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
          />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={departmentFilter} onValueChange={handleDepartmentFilterChange}>
            <SelectTrigger className="w-48 bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departmentOptions.map((department) => (
                <SelectItem key={department} value={department}>
                  {department}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
            <SelectTrigger className="w-36 bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            disabled={!isFilterActive}
            onClick={handleResetFilters}
          >
            <RotateCcw size={14} /> Reset
          </Button>
        </div>
      </div>

      {/* JD Table */}
      <div className="overflow-hidden rounded-xl border border-border-subtle bg-card shadow-sm">
        <Table className="min-w-240">
          <TableHeader className="bg-bg-main">
            <TableRow className="border-border-subtle hover:bg-transparent">
              {[
                'Job Title',
                'Department',
                'Location',
                'Total Applicants',
                'Created Date',
                'Status',
              ].map((label) => (
                <TableHead
                  key={label}
                  className="h-11 px-4 text-xs font-semibold uppercase tracking-wider text-text-subtle"
                >
                  {label}
                </TableHead>
              ))}
              <TableHead className="h-11 px-4 text-right text-xs font-semibold uppercase tracking-wider text-text-subtle">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="px-4 py-8 text-center text-sm text-text-subtle">
                  Loading job descriptions...
                </TableCell>
              </TableRow>
            ) : paginatedJds.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="px-4 py-8 text-center text-sm text-text-subtle">
                  No job descriptions match the current search or filters.
                </TableCell>
              </TableRow>
            ) : (
              paginatedJds.map((jd) => {
                const statusBadge = STATUS_BADGES[jd.status] ?? STATUS_BADGES.CLOSE;
                const applicantCount = jd.candidate_count ?? 0;

                return (
                  <TableRow
                    key={jd.id}
                    className="cursor-pointer border-border-subtle hover:bg-bg-main"
                    onClick={() => navigate(`/jobs/${jd.id}/edit`)}
                  >
                    <TableCell className="px-4 py-3">
                      <div className="flex max-w-70 items-center gap-3">
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
                          <Briefcase size={16} />
                        </span>
                        <span className="truncate font-semibold text-text-main" title={jd.title}>
                          {jd.title}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3 font-medium text-text-main">
                      {jd.department || 'N/A'}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-text-subtle">
                      <span
                        className="flex max-w-55 items-center gap-1.5"
                        title={jd.location || undefined}
                      >
                        <MapPin size={14} className="shrink-0" />
                        <span className="truncate">{jd.location || 'N/A'}</span>
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary-light px-3 py-1 text-xs font-semibold text-primary transition-colors hover:border-primary/50"
                        onClick={(event) => {
                          event.stopPropagation();
                          setApplicantsJd(jd);
                        }}
                      >
                        <Users size={14} />
                        {applicantCount} {applicantCount === 1 ? 'Applicant' : 'Applicants'}
                      </button>
                    </TableCell>
                    <TableCell className="px-4 py-3 font-mono text-sm text-text-subtle">
                      {formatCreatedDate(jd.created_at)}
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusBadge.className}`}
                      >
                        <span className={`size-1.5 rounded-full ${statusBadge.dot}`} />
                        {statusBadge.label}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Actions for ${jd.title}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <DropdownMenuItem onSelect={() => navigate(`/jobs/${jd.id}/edit`)}>
                            <Pencil size={14} /> Edit JD
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => navigate(buildActiveCandidatesUrl(jd.id))}
                          >
                            <ExternalLink size={14} /> Open in Candidates
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-4 py-3">
          <div className="text-sm text-text-subtle">
            Showing{' '}
            <span className="font-semibold text-text-main">
              {filteredJds.length === 0 ? 0 : pageStart + 1}
            </span>{' '}
            to{' '}
            <span className="font-semibold text-text-main">
              {Math.min(pageStart + PAGE_SIZE, filteredJds.length)}
            </span>{' '}
            of <span className="font-semibold text-text-main">{filteredJds.length}</span> JDs
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setCurrentPage(page - 1)}
            >
              <ChevronLeft size={14} /> Previous
            </Button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <Button
                key={pageNumber}
                type="button"
                variant={pageNumber === page ? 'primary' : 'ghost'}
                size="sm"
                className="min-w-8"
                aria-current={pageNumber === page ? 'page' : undefined}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setCurrentPage(page + 1)}
            >
              Next <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </div>

      <JobApplicantsModal jd={applicantsJd} onClose={() => setApplicantsJd(null)} />
    </div>
  );
};

export default JDManagement;
